/**
 * Wellness metrics grid (spec §16) and the coach's observation list (§17).
 * Values with context, no letter grades; observations are dismissible and
 * ranked by dollar impact.
 */
import { useMemo, useState } from 'react';
import type { Plan } from '../model/types';
import type { ProjectionResult } from '../engine/projection';
import { computeWellness } from '../engine/wellness';
import { runCoach } from '../engine/coach';
import { fmtCompactUsd } from './format';

interface Props {
  plan: Plan;
  projection: ProjectionResult;
  claimingDeltaNetWorth?: number;
}

export function CoachCard({ plan, projection, claimingDeltaNetWorth }: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const observations = useMemo(
    () => runCoach({ plan, projection, claimingDeltaNetWorth }),
    [plan, projection, claimingDeltaNetWorth],
  );
  const visible = observations.filter((o) => !dismissed.has(o.key));
  if (visible.length === 0) return null;

  return (
    <section className="card" aria-label="Coach observations">
      <h2 className="section-title">
        Worth a look
        <span className="note">
          plain rules reading your projection — what was found, why it matters, what to change
        </span>
      </h2>
      {visible.map((o) => (
        <div key={o.key} className="coach-item">
          <div className="coach-head">
            <strong>{o.title}</strong>
            {o.impact != null && (
              <span className="coach-impact">{fmtCompactUsd(o.impact)} {o.impactLabel}</span>
            )}
            <button
              className="coach-dismiss"
              aria-label={`Dismiss: ${o.title}`}
              onClick={() => setDismissed(new Set([...dismissed, o.key]))}
            >
              ×
            </button>
          </div>
          <p className="coach-body">
            {o.found} {o.why} <strong>{o.action}</strong>
          </p>
        </div>
      ))}
    </section>
  );
}

export function WellnessCard({ plan, projection, claimingDeltaNetWorth }: Props) {
  const metrics = useMemo(
    () => computeWellness({ plan, projection, claimingDeltaNetWorth }),
    [plan, projection, claimingDeltaNetWorth],
  );

  return (
    <section className="card" aria-label="Financial wellness">
      <h2 className="section-title">
        Financial wellness
        <span className="note">
          {metrics.length} measures with context — no single score, because a single score hides which lever to pull
        </span>
      </h2>
      <div className="wellness-grid">
        {metrics.map((m) => (
          <div key={m.key} className={`wellness-tile status-${m.status}`}>
            <div className="wellness-label">
              <span className={`dot dot-${m.status}`} aria-hidden="true" />
              {m.label}
            </div>
            <div className="wellness-value">{m.value}</div>
            <div className="wellness-detail">{m.detail}</div>
            {m.improve && <div className="wellness-improve">{m.improve}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}
